import BuilderModal from "../../Layouts/Modal/BuilderModal";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import MaterialIcon from "../../components/BuilderComponents/MaterialIcon";
import ImportAppModal from "./ImportAppModal";
import SingleSelectBox from "../../components/FormComponents/SingleSelectBox";
import Button from "../../components/FormComponents/Button";
import MultiRadio from "../../components/FormComponents/MultiRadio";
import { useModalContext } from "../../context/ModalContext";
import axiosInstance, { handleApiError } from "../../services/axiosService";
import { toastError, toastSuccess } from "../../utils/toastUtils";
import { saveFile } from "../../commonFunctions/fileFolderConfig";
import useFetchData from "../../hooks/useFetchData";
import { IS_PRODUCTION, IS_SANDBOX } from "../../constant/constant";

const ApplicationActionsPopover = ({
  openAppEditModal,
  app,
  setAppData,
  destroyApplicationLink,
  deleteApplication,
  deployAction,
  createApplicationLink,
  onLock
}) => {
  const [isVersionOpen, setIsVersionOpen] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [isRollbackVersionOpen, setIsRollbackVersionOpen] = useState(false);
  const [validateFilesOpen, setValidateFilesOpen] = useState(false);
  const { getUserConfirmation } = useModalContext();
  const { orgId } = useParams();

  const closeVersionModal = useCallback(() => setIsVersionOpen(""), []);

  const handleGeneratePdf = useCallback(async () => {
    try {
      const { message } = await axiosInstance.get(`/app-document/${orgId}/${app.id}`, {
        headers: { "Zino-App-Id": app.id }
      });
      toastSuccess(message);
    } catch (error) {
      handleApiError(error);
    }
  }, [orgId, app.id]);

  const handleDownloadDump = useCallback(async () => {
    try {
      const response = await axiosInstance.get(`/app/dump`, {
        headers: { "Zino-App-Id": app.id },
        responseType: "blob"
      });
      saveFile(response, "app-export.zip");
    } catch (error) {
      handleApiError(error);
    }
  }, [app.id]);

  const handleDownloadConfig = useCallback(async () => {
    try {
      const response = await axiosInstance.get(`/app/config/export`, {
        headers: { "Zino-App-Id": app.id },
        responseType: "blob"
      });
      saveFile(response, "config.zip");
    } catch (error) {
      handleApiError(error);
    }
  }, [app.id]);

  const deployInProduction = useCallback(async () => {
    const confirmed = await getUserConfirmation("Are you sure you want to deploy to production?");
    if (confirmed) await deployAction("prod");
  }, [getUserConfirmation, deployAction]);

  const handleLockApp = useCallback(async () => {
    const confirmed = await getUserConfirmation(
      `<div style="text-align: center;">
        <strong>Are you sure you want to lock "${app.name}"?</strong>
        <p style="margin-top: 10px; color: #666;">This will prevent all users (including you) from making changes until unlocked.</p>
      </div>`
    );
    if (confirmed && onLock) onLock();
  }, [getUserConfirmation, app.name, onLock]);

  const updateApplicationVersion = useCallback(
    async (cluster, version, versionName) => {
      if (!version) return toastError("Select a version");

      const url =
        isVersionOpen === "prod" ? `/application/version/${version}` : `/application/staging/version/${version}`;

      try {
        const { message } = await axiosInstance.put(
          url,
          { cluster },
          {
            headers: { "Zino-App-Id": app.id }
          }
        );

        setAppData((prev) => ({
          ...prev,
          ...(isVersionOpen === "prod"
            ? { version: versionName, cluster }
            : { staging_version: versionName, staging_cluster: cluster })
        }));

        setIsVersionOpen("");
        toastSuccess(message);
      } catch (error) {
        handleApiError(error);
      }
    },
    [isVersionOpen, app.id, setAppData]
  );

  const handleSaveRollbackAppVersion = useCallback(
    async (version) => {
      try {
        const { message } = await axiosInstance.post(`/application/rollback/${version}`, null, {
          headers: { "Zino-App-Id": app.id }
        });
        setIsRollbackVersionOpen(false);
        toastSuccess(message);
      } catch (error) {
        handleApiError(error);
      }
    },
    [app.id]
  );

  const stagingActions = useMemo(
    () => [
      {
        name: "Start",
        icon: "play_arrow",
        onClick: () => createApplicationLink("stage"),
        show: app.staging_status === "stopped"
      },
      {
        name: "Open Web",
        icon: "open_in_new",
        onClick: () => window.open(app.staging_url, "_blank"),
        show: app.staging_status === "live"
      },
      {
        name: "Deploy",
        icon: "rocket_launch",
        onClick: () => deployAction("stage"),
        show: app.staging_status === "live"
      },
      {
        name: "Stop",
        icon: "destruction",
        onClick: () => destroyApplicationLink("stage"),
        show: ["live", "starting"].includes(app.staging_status)
      },
      {
        name: "Set Version",
        icon: "decimal_decrease",
        onClick: () => setIsVersionOpen("stage"),
        show: true
      }
    ],
    [app.staging_status, app.staging_url, createApplicationLink, deployAction, destroyApplicationLink]
  );

  const productionActions = useMemo(
    () => [
      {
        name: "Start",
        icon: "play_arrow",
        onClick: () => createApplicationLink("prod"),
        show: app.status === "stopped"
      },
      {
        name: "Open Web",
        icon: "open_in_new",
        onClick: () => window.open(app.web_url, "_blank"),
        show: app.status === "live"
      },
      {
        name: "Deploy",
        icon: "rocket_launch",
        onClick: deployInProduction,
        show: app.status === "live"
      },
      {
        name: "Stop",
        icon: "destruction",
        onClick: () => destroyApplicationLink("prod"),
        show: ["live", "starting"].includes(app.status)
      },
      {
        name: "Set Version",
        icon: "decimal_decrease",
        onClick: () => setIsVersionOpen("prod"),
        show: true
      }
    ],
    [app.status, app.web_url, createApplicationLink, deployInProduction, destroyApplicationLink]
  );

  const otherActions = useMemo(
    () => [
      {
        name: "Edit App",
        icon: "edit",
        onClick: () =>
          openAppEditModal?.({
            description: app.description,
            name: app.name,
            id: app.id,
            logo: app.logo
          }),
        show: true
      },
      {
        name: "Rollback Version",
        icon: "decimal_decrease",
        onClick: () => setIsRollbackVersionOpen(true),
        show: true
      },
      {
        name: "Download",
        icon: "download",
        onClick: handleDownloadDump,
        show: true
      },
      {
        name: "Upload",
        icon: "upload",
        onClick: () => setImportOpen(true),
        show: true
      },
      {
        name: "Download Config",
        icon: "download",
        onClick: handleDownloadConfig,
        show: true
      },
      {
        name: "Generate Req. Pdf",
        icon: "notes",
        onClick: handleGeneratePdf,
        show: true
      },
      {
        name: "Validate Files",
        icon: "fact_check",
        onClick: () => setValidateFilesOpen(true),
        show: true
      },
      {
        name: "Lock App",
        icon: "lock",
        onClick: handleLockApp,
        show: true,
        variant: "warning"
      },
      {
        name: "Delete App",
        icon: "delete",
        onClick: deleteApplication,
        show: true,
        variant: "danger"
      }
    ],
    [
      app,
      openAppEditModal,
      handleDownloadDump,
      handleDownloadConfig,
      handleGeneratePdf,
      handleLockApp,
      deleteApplication
    ]
  );

  const ActionItem = ({ action }) => {
    const getVariantColor = () => {
      if (action.variant === "danger") return "#d32f2f";
      if (action.variant === "warning") return "#ed6c02";
      return "inherit";
    };

    return (
      <div
        className="popup_list_items"
        onClick={(e) => {
          e.stopPropagation();
          action.onClick();
        }}
        style={{
          cursor: "pointer",
          padding: "10px 12px",
          display: "flex",
          alignItems: "center",
          gap: "12px",
          borderRadius: "6px",
          transition: "background-color 0.2s",
          color: getVariantColor()
        }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.04)")}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
      >
        <MaterialIcon name={action.icon} className="action" style={{ color: getVariantColor() }} />
        <span className="action_name" style={{ color: getVariantColor() }}>
          {action.name}
        </span>
      </div>
    );
  };

  const ActionSection = ({ title, actions }) => (
    <div style={{ flex: 1, margin: "0 10px" }}>
      <h3 style={{ padding: "4px 10px", marginBottom: "8px", fontSize: "16px", fontWeight: 600 }}>{title}</h3>
      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
        {actions
          .filter((a) => a.show)
          .map((action, idx) => (
            <ActionItem key={idx} action={action} />
          ))}
      </div>
    </div>
  );

  return (
    <>
      <div className="d_flex p_3">
        {IS_SANDBOX && <ActionSection title="Staging" actions={stagingActions} />}
        {IS_PRODUCTION && <ActionSection title="Production" actions={productionActions} />}
        <ActionSection title="Other Actions" actions={otherActions} />
      </div>

      <BuilderModal isOpen={!!isVersionOpen} onClose={closeVersionModal} modalName="Set Application Version">
        <SetAppVersion
          id={app.id}
          version={isVersionOpen === "stage" ? app.staging_version : app.version}
          cluster={isVersionOpen === "stage" ? app.staging_cluster : app.cluster}
          onSave={updateApplicationVersion}
        />
      </BuilderModal>

      <BuilderModal
        isOpen={isRollbackVersionOpen}
        onClose={() => setIsRollbackVersionOpen(false)}
        modalName="Rollback Application Version"
      >
        <RollbackAppVersion appId={app.id} onSave={handleSaveRollbackAppVersion} />
      </BuilderModal>

      <BuilderModal isOpen={importOpen} onClose={() => setImportOpen(false)} modalName="Import App">
        <ImportAppModal id={app.id} onClose={() => setImportOpen(false)} />
      </BuilderModal>

      <BuilderModal
        isOpen={validateFilesOpen}
        onClose={() => setValidateFilesOpen(false)}
        modalName="Validate Uploaded Files"
      >
        {validateFilesOpen && <ValidateAppFiles appId={app.id} />}
      </BuilderModal>
    </>
  );
};

const ValidateAppFiles = ({ appId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError("");
      try {
        // full audit lists blobs + scans instance data; allow well beyond the default 20s
        const { data } = await axiosInstance.get(`/validate-app-files`, {
          params: { app_id: appId },
          headers: { "Zino-App-Id": appId },
          timeout: 300000
        });
        if (!cancelled) setResult(data);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.message || err.message || "Validation failed");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [appId]);

  if (loading) {
    return (
      <div style={{ padding: "24px", textAlign: "center" }}>
        Validating uploaded files against storage… this can take a minute for large apps.
      </div>
    );
  }
  if (error) {
    return <div style={{ padding: "24px", color: "#d32f2f" }}>Error: {error}</div>;
  }

  const reports = result?.reports || [];
  const totalFindings = result?.total_findings ?? 0;
  const maxRows = 200;

  if (reports.length === 0) {
    return (
      <div style={{ padding: "24px", textAlign: "center" }}>
        No deployed databases found for this app — nothing to validate.
      </div>
    );
  }

  return (
    <div style={{ padding: "8px 4px", maxHeight: "70vh", overflowY: "auto" }}>
      <div
        style={{
          padding: "10px 12px",
          marginBottom: "12px",
          borderRadius: "6px",
          fontWeight: 600,
          backgroundColor: totalFindings === 0 ? "#e8f5e9" : "#fdecea",
          color: totalFindings === 0 ? "#2e7d32" : "#d32f2f"
        }}
      >
        {totalFindings === 0
          ? "All uploaded files are present in storage."
          : `${totalFindings} issue(s) found — some uploaded files are missing or empty in storage.`}
      </div>

      {reports.map((r) => (
        <div key={r.db} style={{ marginBottom: "16px" }}>
          <div style={{ fontWeight: 600, marginBottom: "6px" }}>
            {r.env} — {r.file_info_count} file records, {r.instance_ref_count} references, {r.blob_count} blobs in
            storage, {r.findings.length} issue(s)
          </div>
          {r.error && <div style={{ color: "#d32f2f", marginBottom: "6px" }}>Error: {r.error}</div>}
          {r.findings.length > 0 && (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
              <thead>
                <tr style={{ textAlign: "left", borderBottom: "1px solid #ddd" }}>
                  <th style={{ padding: "6px" }}>Issue</th>
                  <th style={{ padding: "6px" }}>File</th>
                  <th style={{ padding: "6px" }}>Details</th>
                </tr>
              </thead>
              <tbody>
                {r.findings.slice(0, maxRows).map((f, idx) => (
                  <tr key={idx} style={{ borderBottom: "1px solid #f0f0f0" }}>
                    <td style={{ padding: "6px", whiteSpace: "nowrap" }}>{f.category}</td>
                    <td style={{ padding: "6px", wordBreak: "break-all" }}>{f.file}</td>
                    <td style={{ padding: "6px" }}>{f.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {r.findings.length > maxRows && (
            <div style={{ padding: "6px", color: "#666" }}>
              …and {r.findings.length - maxRows} more (use the API/CLI for the full list)
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const convertToLocalTime = (utcTimestamp) => {
  return new Date(utcTimestamp).toLocaleString();
};

const RollbackAppVersion = ({ appId, onSave }) => {
  const headers = useMemo(() => ({ "Zino-App-Id": appId }), [appId]);
  const { data, loading, error } = useFetchData(`/application/backups`, [appId], [], headers);
  const [selectedVersion, setSelectedVersion] = useState("");

  const versionOptions = useMemo(() => {
    if (!data?.backups) return [];
    return data.backups.map((backup) => ({
      id: `${backup.id}`,
      version_number: `v.${backup.version_number}`,
      created_at: convertToLocalTime(backup.created_at),
      comment: backup.comment
    }));
  }, [data]);

  if (loading) return <div style={{ padding: "20px", textAlign: "center" }}>Loading versions...</div>;
  if (error) return <div style={{ padding: "20px", color: "#d32f2f" }}>Error: {error.message}</div>;

  return (
    <div className="builder_form">
      <SingleSelectBox
        selectedData={selectedVersion}
        options={[{ id: "", version_number: "Select version" }, ...versionOptions]}
        labelKey={["version_number", "created_at"]}
        onChange={(obj) => setSelectedVersion(obj.id)}
        label="Application version"
        placeholder="Select Version"
        isSearch={true}
      />
      <Button onClick={() => onSave(selectedVersion)}>Set version</Button>
    </div>
  );
};

const SetAppVersion = ({ id, version, cluster, onSave }) => {
  const [appVersions, setAppVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState("");
  const [selectedCluster, setSelectedCluster] = useState(cluster);

  useEffect(() => {
    const fetchAppVersions = async () => {
      try {
        const { data } = await axiosInstance.get(`/application/version`, {
          headers: { "Zino-App-Id": id }
        });
        setAppVersions(data);
        const versionObj = data.find((v) => v.version === version);
        if (versionObj) setSelectedVersion(versionObj.id);
      } catch (error) {
        handleApiError(error);
      }
    };
    fetchAppVersions();
  }, [id, version]);

  const handleSave = useCallback(() => {
    const selectedVersionObj = appVersions.find((v) => v.id === selectedVersion);
    if (selectedVersionObj) {
      onSave(selectedCluster, selectedVersion, selectedVersionObj.version);
    }
  }, [appVersions, selectedVersion, selectedCluster, onSave]);

  return (
    <div className="builder_form">
      <SingleSelectBox
        selectedData={selectedVersion}
        options={appVersions}
        labelKey={["version"]}
        onChange={(obj) => setSelectedVersion(obj.id)}
        label="Application version"
        placeholder="Select Version"
        isSearch={true}
      />
      <MultiRadio
        label="Select Cluster"
        options={[
          { id: "kube", name: "kube" },
          { id: "machine", name: "machine" }
        ]}
        selectedValue={selectedCluster}
        onChange={(e) => setSelectedCluster(e.target.value)}
      />
      <Button onClick={handleSave}>Set version</Button>
    </div>
  );
};

export default ApplicationActionsPopover;
